import { useToast } from 'vue-toastification';
import ToastComponent from '~/components/ui/ToastComponent.vue';
import type { DaDataAddress, StoredAddress } from '~/types/Checkout';
import type { QuestionListResponse, ReviewListResponse } from '~/types/Feedback';
import type {
  NotificationsChatItem,
  NotificationsResponse,
  OrderDetails,
  OrderListItem,
  OrdersListResponse,
  PromoCodePageData,
} from '~/types/Profile';
import type { UserDetails, UserDetailsForm } from '~/types/User';

export interface Discounts {
  id: number;
  title: string;
  money_threshold: number;
  discount_percent: number;
  created_at: string;
  updated_at: string;
  sort_order: number;
}

export const useProfileStore = createProfileStore();

function convertFileToBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

export function createProfileStore() {
  return defineStore('profile', {
    state: () => ({
      userDetails: null as UserDetails | null,
      userInfoLoadStatus: 'pending' as 'pending' | 'loading' | 'resolved' | 'failed',
      userAddresses: [] as StoredAddress[],
      loyaltyPointsBurnWarningPeriod: 14,
      totalUnreadNotifications: 0,
    }),

    actions: {
      clearUserDetails() {
        this.userDetails = null;
      },

      async getUserInfo() {
        this.userInfoLoadStatus = 'loading';

        try {
          const response = import.meta.client
            ? await useCustomFetchClient<{ data: UserDetails | null }>('/profile/info')
            : (await useCustomFetch<{ data: UserDetails | null }>('/profile/info')).data.value;

          if (response?.data) {
            this.userDetails = response.data;

            if (response.data.notifications) {
              let total = 0;

              response.data.notifications.forEach((notificationType: NotificationsChatItem) => {
                total += notificationType.unread_count;
              });

              this.totalUnreadNotifications = total;
            } else if (this.userDetails) {
              this.userDetails.notifications = [];
            }
          } else {
            this.userDetails = null;
          }

          this.userInfoLoadStatus = 'resolved';
        } catch (e) {
          this.userInfoLoadStatus = 'failed';

          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async updateUserInfo(userDetailsForm: UserDetailsForm) {
        try {
          const form: any = {};

          for (const [key, value] of Object.entries(userDetailsForm)) {
            if (value) {
              if (key === 'avatar') {
                form[key] = await convertFileToBase64(value);
              } else {
                form[key] = value;
              }
            }
          }

          const { data, error } = await useCustomFetch<{ data: UserDetails }>('/profile/info', {
            method: 'POST',
            body: form,
          });

          const toast = useToast();

          if (data.value?.data) {
            this.userDetails = data.value.data;
            toast.success({ component: ToastComponent, props: { text: 'Данные успешно обновлены' } });
            return data.value.data;
          }
          if (error.value) {
            toast.error({ component: ToastComponent, props: { text: error.value.data.message } });
            return false;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async getNotifications(type: string, page?: number) {
        try {
          const { data } = await useCustomFetch<NotificationsResponse>(
            `/notification/${type}`,
            { query: { page } },
            '1',
            true,
          );

          return data.value ?? null;
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async readNotifications(unreadNotificationsIds: number[]) {
        try {
          await useCustomFetch<NotificationsResponse>('/notification/read', {
            method: 'POST',
            body: { id: unreadNotificationsIds },
          });
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }

          return [];
        }
      },

      async getOrders() {
        try {
          const { query } = useRoute();

          const { data } = await useCustomFetch<{ data: OrdersListResponse }>('/profile/orders', { query });

          if (data.value?.data) {
            if (import.meta.client) setTimeout(() => window.scrollTo(0, 0), 10);

            return data.value.data;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        }
      },

      async getOrderById(orderId: string) {
        try {
          const { data } = await useCustomFetch<{ data: OrderDetails }>(`/profile/orders/${orderId}`);

          if (data.value?.data) {
            return data.value.data;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        }
      },

      async getOrderByUri(orderUri: string) {
        try {
          const { data } = await useCustomFetch<{ data: OrderDetails }>(`/profile/order/${orderUri}`);

          if (data.value?.data) {
            return data.value.data;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        }
      },

      async retryOrderPayment(order: Pick<OrderListItem, 'id' | 'uri'>): Promise<string | null> {
        const endpoint = order.uri ? `/profile/order/${order.uri}/payment` : `/profile/orders/${order.id}/payment`;

        try {
          const { data, error } = await useCustomFetch<{ data: { payment_url: string } }>(endpoint, {
            method: 'POST',
          });

          if (data.value?.data.payment_url) {
            return data.value.data.payment_url;
          }

          const toast = useToast();
          toast.error({
            component: ToastComponent,
            props: { text: error.value?.data?.message ?? 'Не удалось подготовить платёж. Попробуйте ещё раз позже.' },
          });
        } catch (e) {
          const toast = useToast();
          toast.error({
            component: ToastComponent,
            props: { text: 'Не удалось подготовить платёж. Попробуйте ещё раз позже.' },
          });

          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }

        return null;
      },

      async getPaymentStatus(orderId: string) {
        try {
          await useCustomFetch(`/payments/order/process/${orderId}`);
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async getSavedAddresses() {
        try {
          const { data } = await useCustomFetch<{ data: StoredAddress[] }>('/profile/address');

          this.userAddresses = data.value?.data ?? [];
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async saveAddress(body: { delivery_type: string; value: string; data?: DaDataAddress; pvz_id?: string }) {
        try {
          const { error } = await useCustomFetch('/profile/address', { method: 'POST', body });

          const toast = useToast();

          if (error.value) {
            toast.error({ component: ToastComponent, props: { text: error.value.data.message } });
          } else {
            toast.success({ component: ToastComponent, props: { text: 'Данные успешно обновлены' } });
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async getUserReviews(page?: number) {
        try {
          const { data } = await useCustomFetch<ReviewListResponse>('/profile/testimonials', { query: { page } });

          if (data.value) {
            return data.value;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        }
      },

      async getUserQuestions(page?: number) {
        try {
          const { data } = await useCustomFetch<QuestionListResponse>('/profile/questions', { query: { page } });

          if (data.value) {
            return data.value;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        }
      },

      async getPromoCodePageData() {
        try {
          const { data } = await useCustomFetch<{ data: PromoCodePageData }>('/profile/promo-code-request');

          if (data.value) {
            return data.value;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
          return null;
        }
      },

      async requestPromoCode(body: { code: string; channel: string; comment: string }) {
        try {
          const data = await useCustomFetchClient('/profile/promo-code-request', { method: 'POST', body });

          if (data) {
            return data;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }

          console.log(e);
          return null;
        }
      },
    },
  });
}
